import React, { useEffect, useState } from "react";
import {
    createTheme,
    IconButton,
    Modal,
    TextField,
    ThemeProvider,
    Button,
    createStyles,
    makeStyles,
    Theme,
} from "@material-ui/core";
import styles from "./EditUsername.module.css";
import EditIcon from "@material-ui/icons/Edit";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../RootReducer";
import { GetSelfInfo, UpdateSelfInfo } from "../../../../user/UserActions";
import PuiTypography from "../../../../components/elements/PuiTypography";
import { IsDevice } from "../../../../components/function/useDeviceFlag";
import { countCharLength } from "../../../../utils/ValidationUtils";
import { t } from "i18next";

interface EditUsernameProps {
    isOpenModal: boolean;
    beforeUsername: string;
    setEditModalOpen: (target: boolean) => void;
}
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        textField: {
            width: "200px",
            minHeight: IsDevice("iphone") ? "none" : "40px",
            background: "#FFFFFF0D",
            border: "none",
            borderRadius: "5px",
            "& .MuiInputBase-root": {
                "& .MuiInputBase-input": {
                    color: "white",
                    fontFamily: "Yu Gothic",
                },
            },
        },
    })
);
const theme = createTheme({
    palette: {
        secondary: {
            main: "#16ADC1",
        },
    },
});

export default function EditUsername({
    isOpenModal,
    beforeUsername,
    setEditModalOpen,
}: EditUsernameProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { user, isUpdateUserInfo } = useSelector((state: AppState) => ({
        user: state.user.userInfo,
        isUpdateUserInfo: state.user.isUpdateUserInfo,
    }));

    const surname = beforeUsername.split(" ")[0];

    const givenname = beforeUsername.split(" ")[1];

    // User名編集モーダル表示制御
    const [isOpenEditNameModal, ShowEditNameModal] = useState(false);
    const [isErrorInputName, ErrorInputName] = useState(false);
    const [inputSurname, InputSurname] = useState(surname);
    const [inputGivenname, InputGivenname] = useState(givenname);
    const [isUserInput, ChangeUserInputFlag] = useState(false);

    // コンテンツタイトル入力
    const [isErrorSurnameValidation, CheckSurnameValidation] = useState(false);
    const [isErrorGivennameValidation, CheckGivennameValidation] = useState(false);

    const OpenEditNameModal = () => {
        ShowEditNameModal(true);
        setEditModalOpen(true);
    };

    const CloseEditNameModal = () => {
        ShowEditNameModal(false);
        setEditModalOpen(false);
        ErrorInputName(false);
        ChangeUserInputFlag(false);
        InputSurname("");
        InputGivenname("");
    };

    const InputEditSurname = (event: any) => {
        if (countCharLength(event.target.value) > 40) {
            CheckSurnameValidation(true);
            ChangeUserInputFlag(false);
        } else {
            CheckSurnameValidation(false);
            ChangeUserInputFlag(true);
        }

        InputSurname(event.target.value);
    };

    const InputEditGivenname = (event: any) => {
        if (countCharLength(event.target.value) > 40) {
            CheckGivennameValidation(true);
            ChangeUserInputFlag(false);
        } else {
            CheckGivennameValidation(false);
            ChangeUserInputFlag(true);
        }

        InputGivenname(event.target.value);
    };

    const DoneEditInputName = () => {
        if (inputSurname !== "" || (inputGivenname !== "" && user)) {
            CloseEditNameModal();
            dispatch(UpdateSelfInfo(inputGivenname, inputSurname));
        } else {
            ErrorInputName(true);
        }
    };

    useEffect(() => {
        if (isUpdateUserInfo) {
            dispatch(GetSelfInfo());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUpdateUserInfo]);

    return (
        <div>
            <IconButton onClick={OpenEditNameModal}>
                <EditIcon
                    style={{ width: "20px", height: "20px", color: "var(--loading-bg)" }}
                    color="secondary"
                />
            </IconButton>

            {/* STT編集モーダル */}
            <Modal open={isOpenEditNameModal} onClose={CloseEditNameModal}>
                <div className={styles.nameEditModal}>
                    <div className={styles.title}>
                        <PuiTypography size={18}>{t("名前の変更")}</PuiTypography>
                    </div>
                    <div style={{ display: "flex" }}>
                        <div className={styles.modalLeft}>
                            <div className={styles.titleText}>
                                <PuiTypography size={18}>{t("姓")}</PuiTypography>
                            </div>
                            <ThemeProvider theme={theme}>
                                <TextField
                                    className={classes.textField}
                                    color="secondary"
                                    onChange={InputEditSurname}
                                    fullWidth
                                    minRows={1}
                                    maxRows={2}
                                    autoComplete="off"
                                    variant="outlined"
                                    defaultValue={surname}
                                    error={isErrorSurnameValidation}
                                    helperText={
                                        isErrorSurnameValidation && (
                                            <span style={{ color: "var(--studio-btn-bg)" }}>
                                                {t(
                                                    "全角20文字（半角40文字）以内で正しく入力してください"
                                                )}
                                            </span>
                                        )
                                    }
                                />
                            </ThemeProvider>
                        </div>
                        <div className={styles.modalRight}>
                            <div className={styles.titleText}>
                                <PuiTypography size={18}>{t("名")}</PuiTypography>
                            </div>
                            <ThemeProvider theme={theme}>
                                <TextField
                                    className={classes.textField}
                                    color="secondary"
                                    onChange={InputEditGivenname}
                                    fullWidth
                                    minRows={1}
                                    maxRows={2}
                                    autoComplete="off"
                                    variant="outlined"
                                    defaultValue={givenname}
                                    error={isErrorGivennameValidation}
                                    helperText={
                                        isErrorGivennameValidation && (
                                            <span style={{ color: "var(--studio-btn-bg)" }}>
                                                {t(
                                                    "全角20文字（半角40文字）以内で正しく入力してください"
                                                )}
                                            </span>
                                        )
                                    }
                                />
                            </ThemeProvider>
                        </div>
                    </div>
                    <div className={styles.nameEditModalButton}>
                        <div className={styles.cancelButton}>
                            <Button
                                type="submit"
                                onClick={CloseEditNameModal}
                                style={{
                                    marginRight: "16px",
                                    color: "#9C9C9C",
                                    fontFamily: "noto-sans-cjk-jp,sans-serif",
                                    border: "solid 2px #5D5D5D",
                                    width: "200px",
                                    fontSize: "16px",
                                }}
                            >
                                {t("キャンセル")}
                            </Button>
                            <Button
                                type="submit"
                                onClick={DoneEditInputName}
                                disabled={!isUserInput}
                                style={
                                    isUserInput
                                        ? {
                                              color: "white",
                                              fontWeight: "bold",
                                              fontFamily: "noto-sans-cjk-jp,sans-serif",
                                              backgroundColor: "var(--studio-btn-bg)",
                                              width: "200px",
                                              fontSize: "16px",
                                          }
                                        : {
                                              color: "var(--loading-bg)",
                                              fontSize: "16px",
                                              width: "200px",
                                              fontWeight: "bold",
                                              fontFamily: "noto-sans-cjk-jp,sans-serif",
                                              backgroundColor: "var(--button-bg)",
                                          }
                                }
                            >
                                {t("更新")}
                            </Button>
                        </div>
                    </div>
                    <div
                        className={styles.nameEditModalAlart}
                        style={{ visibility: isErrorInputName ? "visible" : "hidden" }}
                    >
                        {t("※入力してください")}
                    </div>
                </div>
            </Modal>
        </div>
    );
}
